import { useState, useRef, useEffect } from 'react';
import { C, F, Card } from '../../../../tokens';
import { callClaude, AI_ERROR, AI_EMPTY } from '../../../../tokens';
import { AREAS, seedAreas } from './areas';
import { buildSystemPrompt } from './systemPrompt';
import { buildUnderstandingReviewPrompt } from './understandingReviewPrompt';
import { listSessions, createSession, listMessages, appendMessage, listUnits, createUnit } from '../../persistence';
import { groupUnitsByDomain } from '../../domainState';
import { DOMAINS, UNASSIGNED } from '../../../../config/domains';

// ─────────────────────────────────────────────────────────────────────────────
// onStateChange CONTRACT (pinned — Diff 6 / CompassModule MUST match exactly):
//   onStateChange is called with an UPDATER FUNCTION: (prev) => next.
//   CompassModule holds the state in useState and passes its setter DIRECTLY:
//     onStateChange={setBusinessUnderstanding}
//   i.e. this component calls onStateChange(prev => ({ ...prev, key: value })),
//   exactly like React's functional setState. CompassModule does NOT merge or
//   wrap — it hands over its raw setter.
//
// PERSISTED keys (survive Compass internal tab switches via persistedState):
//   messages, input, areas, phase, overallSummary, approvedKeys
// TRANSIENT keys (local only, correctly reset on remount — never persist an
//   in-flight flag): sending, reviewLoading, reviewError, editingKey, editDraft
// ─────────────────────────────────────────────────────────────────────────────

// Strip accidental ```json fences and isolate the outermost {...} before parse.
// Returns a parsed object or null — never throws.
function safeParseReview(raw) {
  if (!raw || typeof raw !== "string") return null;
  let s = raw.trim().replace(/^```(?:json)?/i, "").replace(/```$/,"").trim();
  const first = s.indexOf("{"), last = s.lastIndexOf("}");
  if (first === -1 || last === -1 || last < first) return null;
  try { return JSON.parse(s.slice(first, last + 1)); } catch { return null; }
}

// Coerce whatever the model returned into the locked six-area shape, defensively.
function mergeAreas(parsedAreas) {
  const out = seedAreas();
  if (!parsedAreas || typeof parsedAreas !== "object") return out;
  for (const a of AREAS) {
    const p = parsedAreas[a.key];
    if (!p || typeof p !== "object") continue;
    const status = ["empty","partial","understood"].includes(p.status) ? p.status : "empty";
    let confidence = Number(p.confidence);
    if (!Number.isFinite(confidence)) confidence = 0;
    confidence = Math.max(0, Math.min(100, Math.round(confidence)));
    out[a.key] = {
      understanding: typeof p.understanding === "string" ? p.understanding : "",
      status,
      confidence,
      openQuestion: (typeof p.openQuestion === "string" && p.openQuestion.trim()) ? p.openQuestion.trim() : null,
    };
  }
  return out;
}

// Belt-and-suspenders for the anti-self-answering prompt rule: if the model
// slips and writes the next speaker's line, cut the reply at that marker so we
// only ever render Compass's single turn. Case-insensitive, allows leading
// whitespace. Never returns empty — falls back to the original text so we
// never show a blank bubble.
function stripHallucinatedTurns(text) {
  if (!text || typeof text !== "string") return text;
  const lines = text.split("\n");
  let cut = -1;
  for (let i = 0; i < lines.length; i++) {
    if (/^\s*(CEO|User|You|Compass)\s*:/i.test(lines[i])) { cut = i; break; }
  }
  if (cut === -1) return text;
  const kept = lines.slice(0, cut).join("\n").trim();
  return kept || text;
}

// Mirrors the kind vocabulary enforced by intel.understanding_units. Kept as a
// plain list so the capture selector and the database CHECK stay legible
// side by side.
const CAPTURE_KINDS = ["observation","decision","goal","priority","note","next_step"];

const SELECT_STYLE = {
  background:"#fff", border:`1px solid ${C.border}`, borderRadius:6,
  padding:"5px 8px", fontFamily:F.body, fontSize:11, color:C.charcoal, cursor:"pointer",
};

function confColor(pct) { return pct >= 70 ? C.green : pct >= 34 ? C.amber : C.charLight; }
function statusPill(status) {
  if (status === "understood") return { label:"Understood", color:C.green, bg:C.greenBg, bd:C.greenBd };
  if (status === "partial")    return { label:"Partial",    color:C.amber, bg:C.amberBg, bd:C.amberBd };
  return { label:"Not yet discussed", color:C.charLight, bg:C.bgAlt, bd:C.border };
}

export default function BusinessUnderstandingExperience({ companyName, subjectId, persistedState, onStateChange }) {
  const displayName = (companyName && companyName.trim()) ? companyName.trim() : "this business";
  const OPENING_TEXT = `I'd love to get to know ${displayName}. Tell me — in your own words, what does the business actually do, and who for?`;

  // ── Persisted state (rehydrated from persistedState on remount) ─
  const [messages, setMessagesLocal] = useState(
    persistedState?.messages?.length ? persistedState.messages : [{ role:"assistant", text:OPENING_TEXT, ts:null }]
  );
  const [input, setInputLocal]                 = useState(persistedState?.input || "");
  const [areas, setAreasLocal]                 = useState(persistedState?.areas || seedAreas());
  const [phase, setPhaseLocal]                 = useState(persistedState?.phase || "conversing");
  const [overallSummary, setOverallLocal]      = useState(persistedState?.overallSummary || "");
  const [approvedKeys, setApprovedKeysLocal]   = useState(persistedState?.approvedKeys || {});

  // Persist wrappers — each routes its update up through onStateChange(prev=>next).
  function setMessages(fn){ setMessagesLocal(prev=>{ const next=typeof fn==="function"?fn(prev):fn; onStateChange&&onStateChange(p=>({...p,messages:next})); return next; }); }
  function setInput(v){ setInputLocal(v); onStateChange&&onStateChange(p=>({...p,input:v})); }
  function setAreas(fn){ setAreasLocal(prev=>{ const next=typeof fn==="function"?fn(prev):fn; onStateChange&&onStateChange(p=>({...p,areas:next})); return next; }); }
  function setPhase(v){ setPhaseLocal(v); onStateChange&&onStateChange(p=>({...p,phase:v})); }
  function setOverallSummary(v){ setOverallLocal(v); onStateChange&&onStateChange(p=>({...p,overallSummary:v})); }
  function setApprovedKeys(fn){ setApprovedKeysLocal(prev=>{ const next=typeof fn==="function"?fn(prev):fn; onStateChange&&onStateChange(p=>({...p,approvedKeys:next})); return next; }); }

  // ── Transient UI state (local only; correctly reset on remount) ─
  const [sending, setSending]             = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError]     = useState(null);
  const [editingKey, setEditingKey]       = useState(null);
  const [editDraft, setEditDraft]         = useState("");
  const chatRef = useRef(null);

  // ── Persistence state (transient; deliberately NOT routed through
  //    onStateChange — the database is the record, not the lifted copy) ─
  const [sessionId, setSessionId]           = useState(null);
  const [units, setUnits]                   = useState([]);
  const [loading, setLoading]               = useState(true);
  const [loadError, setLoadError]           = useState(null);
  const [persistWarning, setPersistWarning] = useState(null);

  // ── Capture affordance state (transient) ───────────────────────
  const [captureFor, setCaptureFor]       = useState(null);
  const [captureKind, setCaptureKind]     = useState("observation");
  const [captureDomain, setCaptureDomain] = useState(null);
  const [captureText, setCaptureText]     = useState("");
  const [saving, setSaving]               = useState(false);
  const [saveError, setSaveError]         = useState(null);

  // ── Load persisted conversation + understanding ────────────────
  useEffect(() => {
    let cancelled = false;

    if (!subjectId) {
      // A missing subject is a wiring failure, not a mode. Nothing loads,
      // nothing is created, and the composer stays disabled.
      setLoading(false);
      setLoadError("No subject selected");
      return;
    }

    setLoading(true);
    setLoadError(null);

    (async () => {
      try {
        const sessions = await listSessions(subjectId);

        // SESSION LIFECYCLE IS DEFERRED ARCHITECTURE. In M2.1 a session exists
        // only to satisfy the foreign keys in the persistence model; it carries
        // no business meaning. listSessions orders by started_at descending, so
        // element 0 is the most recent — reuse it. One is created only when a
        // subject has none, and never again automatically.
        //
        // What constitutes a NEW session — an engagement boundary, a date roll,
        // an explicit act by the advisor — is a rule a later increment will
        // define. M2.1 deliberately neither guesses that rule nor implies one,
        // which is why sessions appear nowhere in the interface.
        const session = (sessions && sessions.length) ? sessions[0] : await createSession(subjectId);
        if (cancelled) return;
        setSessionId(session.id);

        const msgs = await listMessages(subjectId);
        const rows = await listUnits(subjectId);
        if (cancelled) return;

        if (msgs && msgs.length) {
          // Hydrate the local copy only. The lifted copy in CompassModule is not
          // written here: every mount re-reads from the database, which is now
          // the record for the conversation.
          setMessagesLocal(msgs.map(row => ({
            id: row.id,
            role: row.role,
            text: row.content,
            ts: new Date(row.created_at),
            unsaved: false,
          })));
        }

        setUnits(rows || []);
        setLoading(false);
      } catch {
        if (cancelled) return;
        // What is on screen stays on screen. A load failure must never blank
        // the conversation.
        setLoadError("Couldn't load the saved record for this subject. Anything on screen is unsaved.");
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [subjectId]);

  // ── Conversation turn ──────────────────────────────────────────
  async function handleSend() {
    const text = input.trim();
    if (!text || sending) return;
    // Safety net. The composer is already disabled in this state.
    if (!subjectId || !sessionId) return;

    setInput("");
    setSending(true);
    setPersistWarning(null);

    // The CEO's turn is written before it is shown. If it cannot be saved it
    // was never said, as far as the record is concerned — so nothing is
    // appended and Claude is not called.
    let savedUser;
    try {
      savedUser = await appendMessage(subjectId, { sessionId, role:"user", content:text });
    } catch {
      setInput(text);
      setPersistWarning("That message couldn't be saved, so it wasn't sent. Your text is still in the box — try again.");
      setSending(false);
      return;
    }

    const userMsg = { id:savedUser.id, role:"user", text, ts:new Date(savedUser.created_at), unsaved:false };
    setMessages(p=>[...p, userMsg]);

    try {
      const history = [...messages, userMsg].map(m=>`${m.role==="user"?"CEO":"Compass"}: ${m.text}`).join("\n\n");
      const reply = await callClaude(buildSystemPrompt({ companyName }), history, 2000);
      // callClaude cannot reject: a transport or upstream failure comes back as
      // one of these two strings on the ordinary success path. They are content-
      // shaped, so without this identity check a failed request would be cleaned,
      // written to intel.conversation_messages, given a durable id and rendered
      // as capturable evidence. Strict equality only — the values are ours, and
      // a substring rule would swallow a real reply that happened to quote one.
      if (reply === AI_ERROR || reply === AI_EMPTY) {
        // Local consolation message. Never persisted — it is not evidence.
        setMessages(p=>[...p, { role:"assistant", text:"Sorry, I lost my train of thought there. Could you say that again?", ts:new Date(), unsaved:true }]);
        setPersistWarning("The AI request didn't complete, so nothing came back this time. Your message was saved — try again.");
      } else {
        const cleaned = stripHallucinatedTurns(reply);
        try {
          const savedReply = await appendMessage(subjectId, { sessionId, role:"assistant", content:cleaned });
          setMessages(p=>[...p, { id:savedReply.id, role:"assistant", text:cleaned, ts:new Date(savedReply.created_at), unsaved:false }]);
        } catch {
          // The reply is real and worth reading, so it is shown — but it carries
          // no id and is marked unsaved, which also withholds the Capture control.
          setMessages(p=>[...p, { role:"assistant", text:cleaned, ts:new Date(), unsaved:true }]);
          setPersistWarning("Compass replied, but that reply couldn't be saved. It won't be here when you come back.");
        }
      }
    } catch {
      // Local consolation message. Never persisted — it is not evidence.
      setMessages(p=>[...p, { role:"assistant", text:"Sorry, I lost my train of thought there. Could you say that again?", ts:new Date(), unsaved:true }]);
    }
    setSending(false);
    setTimeout(()=>chatRef.current?.scrollTo({ top:99999, behavior:"smooth" }), 100);
  }

  // ── Understanding Review ───────────────────────────────────────
  async function runReview() {
    const hasUserContent = messages.some(m => m.role === "user" && m.text && m.text.trim());
    if (!hasUserContent) return;
    setPhase("reviewing");
    setReviewError(null);
    setReviewLoading(true);
    try {
      const transcript = messages.map(m=>`${m.role==="user"?"CEO":"Compass"}: ${m.text}`).join("\n\n");
      const raw = await callClaude(buildUnderstandingReviewPrompt({ companyName, transcript }), "Please produce the Understanding Review now as JSON.", 2000);
      const parsed = safeParseReview(raw);
      if (!parsed) { setReviewError("I had trouble compiling the review just now. Want to try again?"); setReviewLoading(false); return; }
      setAreas(mergeAreas(parsed.areas));
      setOverallSummary(typeof parsed.overallSummary === "string" ? parsed.overallSummary : "");
    } catch {
      setReviewError("Something interrupted the review. Your conversation is safe — try again?");
    }
    setReviewLoading(false);
  }

  function approveArea(key) { setAreas(prev=>({ ...prev, [key]:{ ...prev[key], status:"understood" } })); setApprovedKeys(prev=>({ ...prev, [key]:true })); }
  function startCorrect(key) { setEditingKey(key); setEditDraft(areas[key]?.understanding || ""); }
  function saveCorrect(key) {
    const text = editDraft.trim();
    setAreas(prev=>({ ...prev, [key]:{ ...prev[key], understanding:text, status:"understood", confidence:Math.max(prev[key]?.confidence||0, 85), openQuestion:null } }));
    setApprovedKeys(prev=>({ ...prev, [key]:true }));
    setEditingKey(null); setEditDraft("");
  }

  // ── Capture: turn one message into one unit of understanding ───
  function openCapture(m) {
    setCaptureFor(m.id);
    setCaptureText(m.text || "");
    setCaptureKind("observation");
    setCaptureDomain(null);
    setSaveError(null);
  }

  function cancelCapture() {
    setCaptureFor(null);
    setCaptureText("");
    setCaptureKind("observation");
    setCaptureDomain(null);
    setSaveError(null);
  }

  async function saveCapture() {
    const statement = captureText.trim();
    if (!statement || saving || !subjectId || !sessionId || !captureFor) return;
    setSaving(true);
    setSaveError(null);
    try {
      const row = await createUnit(subjectId, {
        kind: captureKind,
        domainKey: captureDomain,
        statement,
        detail: null,
        sessionId,
        sourceMessageId: captureFor,
      });
      // Nothing enters the list until the write has come back.
      setUnits(p=>[...p, row]);
      setCaptureFor(null);
      setCaptureText("");
      setCaptureKind("observation");
      setCaptureDomain(null);
    } catch {
      setSaveError("That couldn't be saved. Your text is still here — try again.");
    }
    setSaving(false);
  }

  // ── Workspace region: Understanding Review (renders only when reviewing) ─
  const reviewRegion = phase === "reviewing" ? (
      <div style={{display:"flex",flexDirection:"column",gap:16}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
          <div style={{fontFamily:F.display,fontSize:18,color:C.charcoal,fontWeight:600}}>Understanding Review</div>
        </div>

        {reviewLoading ? (
          <Card style={{padding:"40px 28px",textAlign:"center"}}>
            <div style={{fontFamily:F.body,fontSize:13,color:C.charMid,fontStyle:"italic"}}>◈ Compiling what I've understood so far…</div>
          </Card>
        ) : reviewError ? (
          <Card style={{padding:"28px",textAlign:"center"}}>
            <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,marginBottom:14}}>{reviewError}</div>
            <button onClick={runReview}
              style={{background:C.navy,color:"#fff",border:"none",borderRadius:8,padding:"9px 18px",fontFamily:F.body,fontSize:12,fontWeight:600,cursor:"pointer"}}>
              Try the review again
            </button>
          </Card>
        ) : (
          <>
            {overallSummary && (
              <Card style={{padding:"18px 20px",background:C.navyFaint,border:`1px solid ${C.navyBorder}`}}>
                <div style={{fontFamily:F.mono,fontSize:9,color:C.navy,textTransform:"uppercase",letterSpacing:".08em",marginBottom:6}}>What I understand so far</div>
                <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,lineHeight:1.6}}>{overallSummary}</div>
              </Card>
            )}

            {AREAS.map(a=>{
              const area = areas[a.key] || { understanding:"", status:"empty", confidence:0, openQuestion:null };
              const isEmpty = area.status === "empty" || !area.understanding;
              const pill = statusPill(area.status);
              const approved = approvedKeys[a.key];
              return (
                <Card key={a.key} style={{padding:"18px 20px"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,marginBottom:8}}>
                    <div style={{fontFamily:F.display,fontSize:15,color:C.charcoal,fontWeight:600}}>{a.label}</div>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      {approved && <span style={{fontFamily:F.mono,fontSize:9,color:C.green,fontWeight:700}}>✓ APPROVED</span>}
                      <span style={{fontFamily:F.mono,fontSize:9,color:pill.color,background:pill.bg,border:`1px solid ${pill.bd}`,borderRadius:5,padding:"2px 8px",textTransform:"uppercase",letterSpacing:".06em"}}>{pill.label}</span>
                    </div>
                  </div>

                  {isEmpty ? (
                    <div style={{fontFamily:F.body,fontSize:12,color:C.charLight,fontStyle:"italic"}}>We haven't talked about this yet.</div>
                  ) : (
                    <>
                      {editingKey === a.key ? (
                        <div style={{display:"flex",flexDirection:"column",gap:8}}>
                          <textarea value={editDraft} onChange={e=>setEditDraft(e.target.value)} rows={4} autoFocus
                            style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:8,padding:"10px 12px",fontFamily:F.body,fontSize:13,color:C.charcoal,lineHeight:1.6,resize:"vertical",boxSizing:"border-box"}}/>
                          <div style={{display:"flex",gap:8}}>
                            <button onClick={()=>saveCorrect(a.key)} disabled={!editDraft.trim()}
                              style={{background:C.navy,color:"#fff",border:"none",borderRadius:7,padding:"7px 14px",fontFamily:F.body,fontSize:12,fontWeight:600,cursor:editDraft.trim()?"pointer":"default",opacity:editDraft.trim()?1:.6}}>Save correction</button>
                            <button onClick={()=>{setEditingKey(null);setEditDraft("");}}
                              style={{background:C.bgAlt,color:C.charMid,border:`1px solid ${C.border}`,borderRadius:7,padding:"7px 14px",fontFamily:F.body,fontSize:12,cursor:"pointer"}}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,lineHeight:1.6,marginBottom:10}}>{area.understanding}</div>
                          {area.openQuestion && (
                            <div style={{fontFamily:F.body,fontSize:12,color:C.navyMid,fontStyle:"italic",marginBottom:10}}>Still curious: {area.openQuestion}</div>
                          )}
                          {/* Confidence */}
                          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
                            <div style={{flex:1,maxWidth:160,height:4,background:C.bgAlt,borderRadius:2,overflow:"hidden"}}>
                              <div style={{width:`${area.confidence}%`,height:"100%",background:confColor(area.confidence),borderRadius:2,transition:"width .3s"}}/>
                            </div>
                            <span style={{fontFamily:F.mono,fontSize:9,color:C.charLight}}>{area.confidence}% confident</span>
                          </div>
                          {/* Approve / Correct */}
                          <div style={{display:"flex",gap:8}}>
                            <button onClick={()=>approveArea(a.key)}
                              style={{background:C.greenBg,color:C.green,border:`1px solid ${C.greenBd}`,borderRadius:7,padding:"6px 14px",fontFamily:F.body,fontSize:12,fontWeight:600,cursor:"pointer"}}>Approve</button>
                            <button onClick={()=>startCorrect(a.key)}
                              style={{background:"transparent",color:C.charMid,border:`1px solid ${C.border}`,borderRadius:7,padding:"6px 14px",fontFamily:F.body,fontSize:12,cursor:"pointer"}}>Correct</button>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </Card>
              );
            })}
          </>
        )}
      </div>
  ) : null;

  // ── Workspace region: Understanding (captured units, by domain) ─
  // Coverage is PROJECTED on every render from the units themselves. The per-
  // domain state string is never stored, never cached, and never written back —
  // the units are the record, and this is only a reading of them.
  const confirmedUnits = units.filter(u => u && u.status === "confirmed");
  const domainGroups = groupUnitsByDomain(confirmedUnits);

  const understandingRegion = (
    <Card style={{padding:"18px 20px"}}>
      <div style={{fontFamily:F.display,fontSize:15,color:C.charcoal,fontWeight:600,marginBottom:3}}>Understanding</div>
      <div style={{fontFamily:F.body,fontSize:12,color:C.charLight,marginBottom:14}}>What has been captured from this conversation, by domain.</div>

      {loadError && (
        <div style={{fontFamily:F.body,fontSize:12,color:C.charcoal,background:C.amberBg,border:`1px solid ${C.amberBd}`,borderRadius:7,padding:"8px 12px",marginBottom:12,lineHeight:1.5}}>
          {loadError}
        </div>
      )}

      {confirmedUnits.length === 0 ? (
        <div style={{fontFamily:F.body,fontSize:12,color:C.charLight,fontStyle:"italic"}}>
          {loading ? "Loading…" : "Nothing captured yet."}
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          {domainGroups.map(g => {
            // The Unsorted bucket earns its row only by holding something.
            if (g.domain.key === UNASSIGNED.key && g.units.length === 0) return null;
            return (
              <div key={g.domain.key || "unsorted"}>
                <div style={{display:"flex",alignItems:"baseline",gap:8}}>
                  <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,fontWeight:600}}>{g.domain.label}</div>
                  <span style={{fontFamily:F.mono,fontSize:9,color:C.charLight,textTransform:"uppercase",letterSpacing:".06em"}}>{g.state}</span>
                </div>
                {g.units.length > 0 && (
                  <ul style={{margin:"6px 0 0",padding:"0 0 0 18px",display:"flex",flexDirection:"column",gap:4}}>
                    {g.units.map(u => (
                      <li key={u.id} style={{fontFamily:F.body,fontSize:12,color:C.charMid,lineHeight:1.6}}>{u.statement}</li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );

  // ── WELCOME phase ──────────────────────────────────────────────
  if (phase === "welcome") {
    return (
      <Card style={{padding:"40px 32px",textAlign:"center"}}>
        <div style={{fontFamily:F.display,fontSize:22,color:C.charcoal,fontWeight:600,marginBottom:12}}>Welcome to Compass</div>
        <div style={{fontFamily:F.body,fontSize:14,color:C.charMid,lineHeight:1.7,maxWidth:520,margin:"0 auto 28px"}}>
          Before anything else, I want to understand {displayName} — in your own words. Not a form, not a setup wizard. Just your story, the way you'd tell a trusted advisor. I'll learn your business before I ever try to advise it.
        </div>
        <button onClick={()=>setPhase("conversing")}
          style={{background:C.navy,color:"#fff",border:"none",borderRadius:8,padding:"12px 24px",fontFamily:F.body,fontSize:13,fontWeight:600,cursor:"pointer"}}>
          Tell me your story →
        </button>
      </Card>
    );
  }

  // ── Conversation (permanent) + workspace region ────────────────
  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
    <Card style={{padding:"20px 22px"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,marginBottom:12}}>
        <div>
          <div style={{fontFamily:F.display,fontSize:15,color:C.charcoal}}>◈ Getting to know {displayName}</div>
          <div style={{fontFamily:F.body,fontSize:12,color:C.charLight,marginTop:2}}>A conversation — Compass is building its understanding as you talk.</div>
          {!subjectId && (
            <div style={{fontFamily:F.body,fontSize:12,color:C.charMid,marginTop:6}}>No subject selected</div>
          )}
        </div>
        <button onClick={runReview}
          style={{flexShrink:0,background:"none",border:"none",color:C.navy,fontFamily:F.body,fontSize:12,fontWeight:600,cursor:"pointer",padding:0,whiteSpace:"nowrap"}}>
          See what you've understood so far →
        </button>
      </div>

      {/* Chat messages */}
      <div ref={chatRef} style={{maxHeight:420,overflowY:"auto",marginBottom:10,display:"flex",flexDirection:"column",gap:10,padding:"4px 0"}}>
        {messages.map((m,i)=>(
          <div key={i} style={{display:"flex",flexDirection:"column",alignItems:m.role==="user"?"flex-end":"flex-start"}}>
            <div style={{maxWidth:"85%",background:m.role==="user"?C.navy:C.surface,color:m.role==="user"?"#fff":C.charcoal,border:m.role==="user"?"none":`1px solid ${C.border}`,borderRadius:m.role==="user"?"12px 12px 2px 12px":"12px 12px 12px 2px",padding:"11px 15px",fontFamily:F.body,fontSize:13,lineHeight:1.7,whiteSpace:"pre-wrap"}}>
              {m.text}
            </div>
            <div style={{fontFamily:F.mono,fontSize:9,color:C.charLight,marginTop:2,padding:"0 4px"}}>
              {m.role==="user"?"You":"Compass"}{m.ts?` · ${m.ts.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}`:""}
            </div>

            {/* Capture appears only for a message that is actually in the
                record. No id or unsaved means no control at all — never a
                disabled one, and never a path to a unit with nothing behind it. */}
            {m.id && !m.unsaved && captureFor !== m.id && (
              <button onClick={()=>openCapture(m)}
                style={{background:"none",border:"none",color:C.navy,fontFamily:F.mono,fontSize:9,textTransform:"uppercase",letterSpacing:".06em",cursor:"pointer",padding:"2px 4px",marginTop:1}}>
                Capture
              </button>
            )}

            {captureFor === m.id && (
              <div style={{width:"85%",marginTop:6,background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:8,padding:"10px 12px",display:"flex",flexDirection:"column",gap:8}}>
                <textarea value={captureText} onChange={e=>setCaptureText(e.target.value)} rows={3} autoFocus
                  placeholder="Say it the way you'd say it back to the CEO…"
                  style={{width:"100%",background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,padding:"8px 10px",fontFamily:F.body,fontSize:12,color:C.charcoal,lineHeight:1.6,resize:"vertical",boxSizing:"border-box"}}/>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  <select value={captureKind} onChange={e=>setCaptureKind(e.target.value)} style={SELECT_STYLE}>
                    {CAPTURE_KINDS.map(k=><option key={k} value={k}>{k.replace("_"," ")}</option>)}
                  </select>
                  <select value={captureDomain || ""} onChange={e=>setCaptureDomain(e.target.value || null)} style={SELECT_STYLE}>
                    <option value="">{UNASSIGNED.label}</option>
                    {DOMAINS.map(d=><option key={d.key} value={d.key}>{d.label}</option>)}
                  </select>
                </div>
                {saveError && (
                  <div style={{fontFamily:F.body,fontSize:11,color:C.charcoal,background:C.amberBg,border:`1px solid ${C.amberBd}`,borderRadius:6,padding:"6px 9px",lineHeight:1.5}}>{saveError}</div>
                )}
                <div style={{display:"flex",gap:8}}>
                  <button onClick={saveCapture} disabled={saving||!captureText.trim()||!sessionId}
                    style={{background:C.navy,color:"#fff",border:"none",borderRadius:7,padding:"6px 14px",fontFamily:F.body,fontSize:12,fontWeight:600,cursor:(saving||!captureText.trim()||!sessionId)?"default":"pointer",opacity:(saving||!captureText.trim()||!sessionId)?.6:1}}>
                    {saving ? "Saving…" : "Save"}
                  </button>
                  <button onClick={cancelCapture}
                    style={{background:"transparent",color:C.charMid,border:`1px solid ${C.border}`,borderRadius:7,padding:"6px 14px",fontFamily:F.body,fontSize:12,cursor:"pointer"}}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        ))}
        {sending && <div style={{display:"flex"}}><div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:"12px 12px 12px 2px",padding:"11px 15px",fontFamily:F.body,fontSize:13,color:C.charMid,fontStyle:"italic"}}>◈ Thinking…</div></div>}
      </div>

      {/* Persistence notice — quiet by design. The conversation is still
          usable; this says only that something didn't reach the record. */}
      {persistWarning && (
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,background:C.amberBg,border:`1px solid ${C.amberBd}`,borderRadius:8,padding:"8px 12px",marginBottom:8}}>
          <span style={{fontFamily:F.body,fontSize:12,color:C.charcoal,lineHeight:1.5}}>{persistWarning}</span>
          <button onClick={()=>setPersistWarning(null)}
            style={{flexShrink:0,background:"none",border:"none",color:C.charMid,fontFamily:F.mono,fontSize:9,textTransform:"uppercase",letterSpacing:".06em",cursor:"pointer",padding:0}}>Dismiss</button>
        </div>
      )}

      {/* Input */}
      <div style={{display:"flex",gap:8}}>
        <input value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&handleSend()}
          disabled={!subjectId||!sessionId}
          placeholder={subjectId ? "Tell Compass about your business…" : "No subject selected"}
          style={{flex:1,background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:8,padding:"10px 14px",fontFamily:F.body,fontSize:13,color:C.charcoal,boxSizing:"border-box",opacity:(!subjectId||!sessionId)?.6:1}}/>
        <button onClick={handleSend} disabled={!input.trim()||sending||!subjectId||!sessionId}
          style={{background:C.navy,color:"#fff",border:"none",borderRadius:8,padding:"10px 18px",fontFamily:F.body,fontSize:12,fontWeight:600,cursor:(!input.trim()||sending||!subjectId||!sessionId)?"default":"pointer",opacity:(!input.trim()||sending||!subjectId||!sessionId)?.6:1}}>Send</button>
      </div>
    </Card>
    {understandingRegion}
    {reviewRegion}
    </div>
  );
}
